import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getSystemSettingsQueryOptions, useSystemSettings } from '@/stores/settings.store'
import { useStageLeadsList, useUpdateInquiry, type StageLeadsFilterOptions } from '@/stores/leads.store'
import {
  useLeadsSelectedTab,
  useLeadsStageFilterOptions,
  useLeadsVisibleStages,
  useLeadsSearchQuery,
  leadsFiltersActions,
} from '@/stores/leads-filters.store'
import { useLeadsSourceOptions } from '@/stores/leads-source-options.store'
import { organizationStore } from '@/stores/organization.store'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getInquiryStageColor } from '@/lib/enum-utils'
import { LayoutGrid, Table as TableIcon, Plus, ChevronRight } from 'lucide-react'
import { z } from 'zod'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { collapseChevronTransitionClass } from '@/lib/animation-variants'
import { AnimatedCollapse } from '@/components/shared/animated-collapse'
import { LeadsKanban, LeadsTable, badgeVariantToTailwind, mapInquiryLiteToLead } from '@/components/leads'
import { LeadsFilterBar } from '@/components/leads/data-table-active-filters'
import { LeadsSearchInput } from '@/components/leads/leads-search-input'
import type { Lead, LeadsKanbanColumnDef } from '@/components/leads'
import { InquirySheet } from '@/components/inquiries/inquiry-sheet'
import { GenerateEventsDialog } from '@/components/leads/id/generate-events-dialog'
import { leadStageMove } from '@/lib/leads'
import { RejectReasonDialog } from '@/components/leads/id/reject-reason-dialog'
import { toast } from 'sonner'
import { canPreloadOrganizationQueries, preloadQueries } from '@/lib/route-preloading'
import { IncomingReferralsPopover } from '@/components/leads/referrals/incoming-referrals-popover'
import { useStore } from '@tanstack/react-store'

export const Route = createFileRoute('/_app/_home/leads/')({
  component: LeadsPage,
  validateSearch: z.object({
    view: z.enum(['kanban', 'table']).optional(),
    // Set by the "New lead referral" notification to open the referrals popover.
    referrals: z.enum(['open']).optional(),
  }),
  loader: ({ context }) => {
    if (!canPreloadOrganizationQueries(context.auth)) return
    const organizationId = organizationStore.state.selectedOrganizationId
    if (!organizationId) return

    // Inquiries are fetched per stage via useStageLeadsList (paginated);
    // connect-query doesn't expose infinite-query options to prefetch here, so
    // we only warm the system-settings cache in the loader.
    preloadQueries(context.queryClient, [
      getSystemSettingsQueryOptions(organizationId),
    ])
  },
})

function LeadsPage() {
  useBreadcrumbFromRoute()

  const { settings, isLoading: isSettingsLoading } = useSystemSettings()
  const stageFilterOptions = useLeadsStageFilterOptions()
  const sourceOptions = useLeadsSourceOptions()
  const searchQuery = useLeadsSearchQuery()
  const { updateInquiryStage } = useUpdateInquiry()
  const navigate = Route.useNavigate()
  const { view: urlView, referrals: referralsParam } = Route.useSearch()
  const organizationId = useStore(organizationStore, (s) => s.selectedOrganizationId) ?? ''
  const storedTab = useLeadsSelectedTab()
  const [createSheetOpen, setCreateSheetOpen] = useState(false)
  const [showGenerateEventsDialog, setShowGenerateEventsDialog] = useState(false)
  const [acceptedInquiryId, setAcceptedInquiryId] = useState<string | null>(null)
  const [showBookingDialog, setShowBookingDialog] = useState(false)
  const [createdEventId, setCreatedEventId] = useState<string | null>(null)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [pendingRejectInquiryId, setPendingRejectInquiryId] = useState<string | null>(null)
  const globalNavigate = useNavigate()

  // Hybrid tab: URL takes priority, localStorage is fallback
  const view = urlView ?? storedTab

  // Derive stages before any early return (hooks must not appear after conditional returns)
  const stages = (settings?.inquiryStages ?? []).filter(
    s => s !== InquiryStageEnum.INQUIRY_STAGE_UNKNOWN
  )

  const { visibleStages, isScopeOverridden } = useLeadsVisibleStages(stages)

  // Handle Kanban drag-and-drop stage updates
  const handleLeadMove = async (itemId: string, newStatus: number, _oldStatus: number, item?: Lead) => {
    switch (leadStageMove(newStatus as InquiryStageEnum, item ?? {})) {
      // Referred is set only when another business accepts a referral; the
      // column is shown but can't be dropped into. Throwing reverts the card.
      case 'refuse-referred':
        toast.error("Leads can't be moved to Referred", {
          description: 'A lead moves there when another business accepts it as a referral.',
        })
        throw new Error('Referred is set by an accepted referral')
      // The board only moves a card once its query refetches, so returning
      // without saving leaves the card where it was.
      case 'refuse-booked':
        toast.error("A booked lead's stage can't change", {
          description: 'Its stage follows the booking. Manage it from the booking instead.',
        })
        return
      // Collect the reject reason first; the dialog saves the stage.
      case 'reject':
        setPendingRejectInquiryId(itemId)
        setShowRejectDialog(true)
        return
      // Booking is what accepts a lead: open booking instead of saving the
      // stage. The card moves to Accepted once the booking exists.
      case 'book':
        setAcceptedInquiryId(itemId)
        setShowGenerateEventsDialog(true)
        return
      case 'update':
        break
    }

    try {
      await updateInquiryStage(itemId, newStatus as InquiryStageEnum)
    } catch (error) {
      console.error('Failed to update inquiry stage:', error)
      // Kanban will revert on error
      throw error
    }
  }

  const handleLeadContextStageChange = (lead: Lead, newStage: InquiryStageEnum) =>
    handleLeadMove(lead.id, newStage, lead.stage, lead)

  // Only show full-page loader while settings (stage definitions) load;
  // per-stage lead queries render their own skeletons
  if (isSettingsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="text-center">
          <div className="mb-2 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="text-muted-foreground">Loading inquiry stages...</p>
        </div>
      </div>
    )
  }

  // With an explicit stage filter, show only those columns. Otherwise show ALL
  // stages so closed columns stay visible as drop targets under Active scope —
  // fetching cards only for the scope's visible stages.
  const leadColumns: LeadsKanbanColumnDef[] = (isScopeOverridden ? visibleStages : stages)
    .map(stage => ({
      status: stage,
      label: formatEnumName(stage, InquiryStageEnum),
      color: badgeVariantToTailwind(getInquiryStageColor(stage)),
      fetchCards: isScopeOverridden || visibleStages.includes(stage),
    }))

  const handleViewChange = (newView: string) => {
    const tab = newView as 'kanban' | 'table'
    // Update both URL and localStorage
    navigate({ search: { view: tab } })
    leadsFiltersActions.setSelectedTab(tab)
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <Tabs value={view} onValueChange={handleViewChange} className="flex flex-col h-full">
        <div className="flex items-center justify-between">
          <TabsList className="w-fit">
            <TabsTrigger value="kanban">
              <LayoutGrid className="size-4" />
              Kanban
            </TabsTrigger>
            <TabsTrigger value="table">
              <TableIcon className="size-4" />
              Table
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            {organizationId && (
              <IncomingReferralsPopover
                organizationId={organizationId}
                defaultOpen={referralsParam === 'open'}
                onClose={() => navigate({ search: (prev) => ({ ...prev, referrals: undefined }), replace: true })}
              />
            )}
            <Button onClick={() => setCreateSheetOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Lead
            </Button>
          </div>
        </div>

        {/* Search + shared filter bar — shown for both kanban and table views */}
        <div className="flex-shrink-0 flex items-center gap-2">
          <LeadsSearchInput
            value={searchQuery}
            onChange={leadsFiltersActions.setSearchQuery}
          />
          <LeadsFilterBar sourceOptions={sourceOptions} />
        </div>

        <TabsContent value="kanban" className="flex-1 min-h-0 mt-4 flex flex-col gap-3">
          <Card className="flex-1 min-h-0 p-6 flex flex-col">
            <LeadsKanban
              columns={leadColumns}
              filterOptions={stageFilterOptions}
              onItemMove={handleLeadMove}
              stages={stages}
              onStageChange={handleLeadContextStageChange}
            />
          </Card>
        </TabsContent>

        <TabsContent value="table" className="flex-1 mt-4">
          <div className="space-y-3">
            {visibleStages.map(stage => (
              <LeadStageSection
                key={stage}
                stage={stage}
                filterOptions={stageFilterOptions}
                stages={stages}
                onStageChange={handleLeadContextStageChange}
                defaultExpanded={
                  stage === InquiryStageEnum.INQUIRY_STAGE_NEW ||
                  stage === InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS
                }
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Lead Sheet */}
      <InquirySheet
        mode="create"
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
      />

      {/* Dialog shown when a lead is moved to Accepted */}
      {acceptedInquiryId && (
        <GenerateEventsDialog
          open={showGenerateEventsDialog}
          onOpenChange={(open) => {
            setShowGenerateEventsDialog(open)
            if (!open) setAcceptedInquiryId(null)
          }}
          inquiryId={acceptedInquiryId}
          onEventsGenerated={(eventId) => {
            setShowGenerateEventsDialog(false)
            setAcceptedInquiryId(null)
            setCreatedEventId(eventId)
            setShowBookingDialog(true)
          }}
        />
      )}

      {/* Dialog shown when a lead is moved to Rejected */}
      {pendingRejectInquiryId && (
        <RejectReasonDialog
          open={showRejectDialog}
          onOpenChange={(open) => {
            setShowRejectDialog(open)
            if (!open) setPendingRejectInquiryId(null)
          }}
          inquiryId={pendingRejectInquiryId}
          onRejected={() => {
            setShowRejectDialog(false)
            setPendingRejectInquiryId(null)
            toast.success('Lead rejected')
          }}
        />
      )}

      {/* Dialog shown when lead has been converted to a booking */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lead Accepted</DialogTitle>
            <DialogDescription>
              This lead has been accepted and converted to a booking. You can view the booking details or stay on this page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>
              Stay on Leads
            </Button>
            <Button
              onClick={() => {
                if (createdEventId) {
                  globalNavigate({ to: '/events/$id', params: { id: createdEventId } })
                }
                setShowBookingDialog(false)
              }}
              disabled={!createdEventId}
            >
              View Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function LeadStageSection({
  stage,
  filterOptions,
  stages,
  onStageChange,
  defaultExpanded = true,
}: {
  stage: InquiryStageEnum
  filterOptions: StageLeadsFilterOptions
  stages: InquiryStageEnum[]
  onStageChange: (lead: Lead, newStage: InquiryStageEnum) => void | Promise<void>
  defaultExpanded?: boolean
}) {
  const [open, setOpen] = useState(defaultExpanded)
  // Keep fetching while collapsed — the header badge needs totalCount
  const {
    inquiries,
    totalCount,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    hasLoaded,
  } = useStageLeadsList(stage, filterOptions)
  const leads = useMemo(() => inquiries.map(mapInquiryLiteToLead), [inquiries])

  const label = formatEnumName(stage, InquiryStageEnum)

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 w-full py-2 px-1 hover:bg-muted/50 rounded text-left"
      >
        <ChevronRight className={cn("h-4 w-4", collapseChevronTransitionClass, open && "rotate-90")} />
        <span className="font-medium">{label}</span>
        {hasLoaded ? (
          <Badge variant="secondary" className="ml-1">
            {hasMore ? `${leads.length} of ${totalCount}` : totalCount}
          </Badge>
        ) : (
          <Skeleton className="ml-1 h-4 w-8" />
        )}
      </button>
      <AnimatedCollapse open={open}>
        <div className="pt-1">
          {!hasLoaded && (
            <Card className="p-6 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </Card>
          )}
          {hasLoaded && leads.length > 0 && (
            <Card className="p-6">
              <LeadsTable
                data={leads}
                paginationResetKey={JSON.stringify(filterOptions)}
                stages={stages}
                onStageChange={onStageChange}
              />
            </Card>
          )}
          {hasLoaded && leads.length === 0 && (
            <p className="text-sm text-muted-foreground px-6 py-2">No leads in this stage</p>
          )}
          {hasLoaded && hasMore && (
            <div className="flex items-center justify-center gap-3 py-1">
              <span className="text-sm text-muted-foreground">
                Showing {inquiries.length} of {totalCount}
              </span>
              <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      </AnimatedCollapse>
    </div>
  )
}
